"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DriveEventsService = exports.DriveEventType = void 0;
const apiService_1 = require("./apiService");
const cache_1 = require("./cache");
const coreEventManager_1 = require("./coreEventManager");
const volumeEventManager_1 = require("./volumeEventManager");
var interface_1 = require("./interface");
Object.defineProperty(exports, "DriveEventType", { enumerable: true, get: function () { return interface_1.DriveEventType; } });
const OWN_VOLUME_POLLING_INTERVAL = 30;
const OTHER_VOLUME_POLLING_INTERVAL = 60;
/**
 * Service for listening to drive events. The service is responsible for
 * managing the subscriptions to the events and notifying the listeners
 * about the new events.
 */
class DriveEventsService {
    telemetry;
    apiService;
    cache;
    subscribedToRemoteDataUpdates = false;
    listeners = [];
    coreEvents;
    volumesEvents;
    logger;
    constructor(telemetry, apiService, driveEntitiesCache) {
        this.telemetry = telemetry;
        this.telemetry = telemetry;
        this.logger = telemetry.getLogger('events');
        this.apiService = new apiService_1.EventsAPIService(apiService);
        this.cache = new cache_1.EventsCache(driveEntitiesCache);
        // FIXME: Allow to pass own core events manager from the public interface.
        this.coreEvents = new coreEventManager_1.CoreEventManager(this.logger, this.apiService, this.cache);
        this.volumesEvents = {};
    }
    /**
     * Loads all the subscribed volumes (including core events) from the
     * cache and starts listening to their events. Any additional volume
     * that is subscribed to later will be automatically started.
     */
    async subscribeToRemoteDataUpdates() {
        if (this.subscribedToRemoteDataUpdates) {
            return;
        }
        await this.loadSubscribedVolumeEventServices();
        this.sendNumberOfVolumeSubscriptionsToTelemetry();
        this.subscribedToRemoteDataUpdates = true;
        await this.coreEvents.startSubscription();
        await Promise.all(Object.values(this.volumesEvents)
            .map((volumeEvents) => volumeEvents.startSubscription()));
    }
    /**
     * Subscribe to given volume. The volume will be polled for events
     * with the polling interval depending on the type of the volume.
     * Own volumes are polled with highest frequency, while others are
     * polled with lower frequency depending on the total number of
     * subscriptions.
     *
     * @param isOwnVolume - Owned volumes are polled with higher frequency.
     */
    async listenToVolume(volumeId, isOwnVolume = false) {
        await this.loadSubscribedVolumeEventServices();
        if (this.volumesEvents[volumeId]) {
            return;
        }
        this.logger.debug(`Creating volume event manager for volume ${volumeId}`);
        const manager = this.createVolumeEventManager(volumeId, isOwnVolume);
        // FIXME: Use dynamic algorithm to determine polling interval for non-own volumes.
        manager.setPollingInterval(isOwnVolume ? OWN_VOLUME_POLLING_INTERVAL : OTHER_VOLUME_POLLING_INTERVAL);
        if (this.subscribedToRemoteDataUpdates) {
            await manager.startSubscription();
            this.sendNumberOfVolumeSubscriptionsToTelemetry();
        }
    }
    async loadSubscribedVolumeEventServices() {
        for (const volumeId of await this.cache.getSubscribedVolumeIds()) {
            if (!this.volumesEvents[volumeId]) {
                const isOwnVolume = await this.cache.isOwnVolume(volumeId) || false;
                this.createVolumeEventManager(volumeId, isOwnVolume);
            }
        }
    }
    sendNumberOfVolumeSubscriptionsToTelemetry() {
        this.telemetry.logEvent({
            eventName: 'volumeEventsSubscriptionsChanged',
            numberOfVolumeSubscriptions: Object.keys(this.volumesEvents).length,
        });
    }
    createVolumeEventManager(volumeId, isOwnVolume) {
        const manager = new volumeEventManager_1.VolumeEventManager(this.logger, this.apiService, this.cache, volumeId, isOwnVolume);
        for (const listener of this.listeners) {
            manager.addListener(listener);
        }
        this.volumesEvents[volumeId] = manager;
        return manager;
    }
    /**
     * Listen to the drive events. The listener will be called with the
     * new events as they arrive.
     *
     * One call always provides events from withing the same volume. The
     * second argument of the callback `fullRefreshVolumeId` is thus single
     * ID and if multiple volumes must be fully refreshed, client will
     * receive multiple calls.
     */
    addListener(callback) {
        // Add new listener to the list for any new event manager.
        this.listeners.push(callback);
        // Add new listener to all existings managers.
        this.coreEvents.addListener(callback);
        for (const volumeEvents of Object.values(this.volumesEvents)) {
            volumeEvents.addListener(callback);
        }
    }
}
exports.DriveEventsService = DriveEventsService;
//# sourceMappingURL=index.js.map